<template>
	<div class="b-slider" :class="[size, type, rootClasses]">
		<div class="b-slider-track" ref="slider">
			<div class="b-slider-fill" :style="barStyle" />
			<template v-if="ticks">
				<progress-step v-for="(val, key) in tickValues" :key="key" :value="val" />
			</template>
			<slot />
			<progress-indicator :tooltip-always="tooltipAlways" v-model="value1" :type="newTooltipType"
				:tooltip="tooltip" :custom-formatter="customFormatter" :indicator="indicator" :format="format"
				:locale="locale" ref="button1" role="slider" :aria-valuenow="value1" :aria-valuemin="min"
				:aria-valuemax="max" aria-orientation="horizontal"
				:aria-label="Array.isArray(ariaLabel) ? ariaLabel[0] : ariaLabel" :aria-disabled="disabled" />
			<progress-indicator :tooltip-always="tooltipAlways" v-model="value2" :type="newTooltipType"
				:tooltip="tooltip" :custom-formatter="customFormatter" :indicator="indicator" :format="format"
				:locale="locale" ref="button2" v-if="isRange" role="slider" :aria-valuenow="value2" :aria-valuemin="min"
				:aria-valuemax="max" aria-orientation="horizontal"
				:aria-label="Array.isArray(ariaLabel) ? ariaLabel[1] : ''" :aria-disabled="disabled" />
		</div>
	</div>
</template>

<script>
import ProgressIndicator from './ProgressIndicator'
import ProgressStep from './ProgressStep'

function bound(val, min, max) {
	return Math.max(min, Math.min(max, val))
}

export default {
	name: 'ProgressBar',
	components: {
		ProgressIndicator,
		ProgressStep
	},
	props: {
		value: {
			type: [Number, Array],
			default: 0
		},
		min: {
			type: Number,
			default: 0
		},
		max: {
			type: Number,
			default: 100
		},
		step: {
			type: Number,
			default: 1
		},
		type: {
			type: String,
			default: 'is-primary'
		},
		size: String,
		ticks: {
			type: Boolean,
			default: false
		},
		tooltip: {
			type: Boolean,
			default: true
		},
		tooltipType: String,
		rounded: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		lazy: {
			type: Boolean,
			default: false
		},
		customFormatter: Function,
		ariaLabel: [String, Array],
		biggerSliderFocus: {
			type: Boolean,
			default: false
		},
		indicator: {
			type: Boolean,
			default: false
		},
		format: {
			type: String,
			default: 'raw',
			validator: (value) => {
				return [
					'raw',
					'percent'
				].indexOf(value) >= 0
			}
		},
		locale: {
			type: [String, Array],
			default: () => {
				return "de-DE"
			}
		},
		tooltipAlways: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			value1: null,
			value2: null,
			isRange: false,
			isProgress: true // Used by Thumb and Tick
		}
	},
	computed: {
		newTooltipType() {
			return this.tooltipType ? this.tooltipType : this.type
		},
		tickValues() {
			if (!this.ticks || this.min > this.max || this.step === 0) return []
			const result = []
			for (let i = this.min + this.step; i < this.max; i = i + this.step) {
				result.push(i)
			}
			return result
		},
		minValue() {
			return Math.min(this.value1, this.value2)
		},
		maxValue() {
			return Math.max(this.value1, this.value2)
		},
		barSize() {
			return this.isRange
				? `${100 * (this.maxValue - this.minValue) / (this.max - this.min)}%`
				: `${100 * (this.value1 - this.min) / (this.max - this.min)}%`
		},
		barStart() {
			return this.isRange
				? `${100 * (this.minValue - this.min) / (this.max - this.min)}%`
				: '0%'
		},
		precision() {
			const precisions = [this.min, this.max, this.step].map((item) => {
				let decimal = ('' + item).split('.')[1]
				return decimal ? decimal.length : 0
			})
			return Math.max(...precisions)
		},
		barStyle() {
			return {
				width: this.barSize,
				left: this.barStart
			}
		},
		rootClasses() {
			return {
				'is-rounded': this.rounded,
				'is-dragging': this.dragging,
				'is-disabled': this.disabled,
				'slider-focus': this.biggerSliderFocus
			}
		}
	},
	watch: {
	},
	methods: {
		setValues(newValue) {
			if (this.min > this.max) {
				return
			}
			if (Array.isArray(newValue)) {
				this.isRange = true
				const smallValue = typeof newValue[0] !== 'number' || isNaN(newValue[0])
					? this.min
					: bound(newValue[0], this.min, this.max)
				const largeValue = typeof newValue[1] !== 'number' || isNaN(newValue[1])
					? this.max
					: bound(newValue[1], this.min, this.max)
				this.value1 = this.isThumbReversed ? largeValue : smallValue
				this.value2 = this.isThumbReversed ? smallValue : largeValue
			} else {
				this.isRange = false
				this.value1 = isNaN(newValue)
					? this.min
					: bound(newValue, this.min, this.max)
				this.value2 = null
			}
		},
		sliderSize() {
			return this.$refs.slider.getBoundingClientRect().width
		},
	},
	created() {
		this.isThumbReversed = false
		this.isTrackClickDisabled = false
		this.setValues(this.value)
	}
}
</script>

<style>
.b-slider,
.b-slider-fill,
.b-slider-thumb,
.b-slider-thumb-wrapper,
.b-slider-track,
.b-slider-tick-label {
	cursor: default !important;
	user-select: none;
}

.b-slider-thumb-wrapper {
	pointer-events: none;
}
</style>
